import React from "react";

import Icon from "@brighthr/component-icon";
import { IconNameType } from "../IconTypes";
import cn from "classnames";

export type AlertProps = React.HTMLAttributes<HTMLDivElement> & {
  fullWidth?: boolean;
  inline?: boolean;
  type: "information" | "success" | "warning" | "error";
  icon?: IconNameType;
  size?: "xs" | "sm" | "base";
  title?: string;
  dismiss?: boolean;
  alertValidationShowState?: boolean;
  border?: boolean;
  className?: string;
  onDismiss?: () => void;
};
const sizeOptions = {
  base: "p-4",
  sm: "py-2 px-4",
  xs: "py-1 px-2",
};
const AlertTypeColours = {
  information: {
    background: "bg-primary-50",
    border: "border-primary-400",
    text: "text-navy-blue-light",
    icon: "fill-primary-400",
  },
  success: {
    background: "bg-tertiary-500",
    border: "border-success-600",
    text: "text-white",
    icon: "fill-success-700",
  },
  warning: {
    background: "bg-warning-100",
    border: "border-warning-600",
    text: "text-warning-800",
    icon: "fill-warning-600",
  },
  error: {
    background: "bg-white",
    border: "border-error-600",
    text: "text-error-800",
    icon: "fill-error-600",
  },
};

const Alert = ({
  fullWidth = false,
  inline = false,
  type = "information",
  icon,
  size = "base",
  title = "",
  children,
  dismiss = false,
  alertValidationShowState = true,
  border = false,
  className = "",
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onDismiss = () => {},
}: AlertProps): React.ReactElement => {
  if (!alertValidationShowState) {
    return <></>;
  }

  return (
    <div
      className={cn(
        "rounded-lg flex flex-col",
        sizeOptions[size],
        { "max-w-md": !fullWidth },
        { "items-center": inline },
        { "border-2": border },
        { [`${AlertTypeColours[type].border}`]: border },
        AlertTypeColours[type].background,
        AlertTypeColours[type].text,
        className
      )}
      role="alert"
      aria-live="polite"
    >
      <div className="w-full flex items-center">
        {icon && (
          <div
            className={cn("pb-2", {
              "mr-2 w-6": size === "xs",
              "pr-4 w-11": size !== "xs",
            })}
          >
            <Icon size={size === "xs" ? 20 : 30} className={`${AlertTypeColours[type].icon}`} iconName={icon} aria-hidden="true" />
          </div>
        )}
        {title && (
          <h3 className="w-full">
            <span className={AlertTypeColours[type].text}>{title}</span>
          </h3>
        )}
      </div>
      <div>{children}</div>
    </div>
  );
};

export default Alert;
